/**
 * Countdown Timer Utility
 * Shared by promotional-banner countdown blocks and countdown-timer sections.
 *
 * Supports:
 *  - Single or multiple time display elements (querySelectorAll for ticker duplicates)
 *  - Combined "YYYY-MM-DD HH:MM" or separate data-end-date + data-end-time attributes
 *  - Optional data-timezone-offset (minutes from UTC) for fixed-timezone countdowns
 *  - Expired state via .countdown-expired element or data-expired-message text
 */

// Two sections request this file — promotional-banner blocks and countdown-timer sections. A
// second top-level `class` declaration is a SyntaxError that kills the rest of the file, so the
// whole module runs once; that first run already initialises every countdown on the page.
if (!window.PromotionalCountdown) {

  class PromotionalCountdown {
    constructor(element) {
      this.element = element;
      this.endDateRaw = element.dataset.endDate;
      this.endTimeRaw = element.dataset.endTime;
      this.timezoneOffset = element.dataset.timezoneOffset;
      this.expiredMessage = element.dataset.expiredMessage;

      // Support multiple elements (ticker duplicates) via querySelectorAll
      this.daysEls = element.querySelectorAll('[data-days]');
      this.hoursEls = element.querySelectorAll('[data-hours]');
      this.minutesEls = element.querySelectorAll('[data-minutes]');
      this.secondsEls = element.querySelectorAll('[data-seconds]');
      this.containerEl = element.querySelector('.countdown-container, .countdown-timer__ticker-wrapper');
      this.expiredEl = element.querySelector('.countdown-expired, .countdown-timer__expired-wrapper');

      // Clean up any existing countdown on this element
      if (element._countdownInstance) {
        element._countdownInstance.destroy();
      }
      element._countdownInstance = this;

      this.init();
    }

    init() {
      if (!this.endDateRaw) {
        console.warn('Countdown: No end date provided');
        return;
      }

      try {
        this.targetDate = this.parseDate();

        if (isNaN(this.targetDate)) {
          throw new Error('Invalid date provided');
        }

        if (this.targetDate < Date.now()) {
          this.showExpired();
          return;
        }

        this.updateCountdown();
        this.intervalId = setInterval(() => this.updateCountdown(), 1000);
      } catch (error) {
        console.error('Countdown initialization error:', error.message);
        this.showExpired();
      }
    }

    parseDate() {
      var datePart, timePart;

      if (this.endTimeRaw) {
        // Separate data-end-date and data-end-time attributes
        datePart = this.endDateRaw;
        timePart = this.endTimeRaw;
      } else if (this.endDateRaw.includes(' ')) {
        // Combined "YYYY-MM-DD HH:MM" format
        var parts = this.endDateRaw.split(' ');
        datePart = parts[0];
        timePart = parts[1];
      } else {
        // Date only, default to 23:59
        datePart = this.endDateRaw;
        timePart = '23:59';
      }

      if (!datePart || !timePart) {
        throw new Error('Invalid date format');
      }

      var dParts = datePart.split('-');
      var tParts = timePart.split(':');
      var year = parseInt(dParts[0], 10);
      var month = parseInt(dParts[1], 10) - 1;
      var day = parseInt(dParts[2], 10);
      var hours = parseInt(tParts[0], 10);
      var minutes = parseInt(tParts[1], 10);

      if (this.timezoneOffset) {
        // Convert from specified timezone offset (minutes from UTC) to UTC timestamp
        var offsetMinutes = parseInt(this.timezoneOffset, 10);
        return Date.UTC(year, month, day, hours, minutes, 0) - offsetMinutes * 60 * 1000;
      } else {
        // Use browser's local timezone
        return new Date(year, month, day, hours, minutes, 0).getTime();
      }
    }

    destroy() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    updateCountdown() {
      var distance = this.targetDate - Date.now();

      if (distance < 0) {
        this.showExpired();
        return;
      }

      var days = Math.floor(distance / (1000 * 60 * 60 * 24));
      var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      var seconds = Math.floor((distance % (1000 * 60)) / 1000);

      var pad = this.padZero;
      this.daysEls.forEach(function(el) { el.textContent = pad(days); });
      this.hoursEls.forEach(function(el) { el.textContent = pad(hours); });
      this.minutesEls.forEach(function(el) { el.textContent = pad(minutes); });
      this.secondsEls.forEach(function(el) { el.textContent = pad(seconds); });
    }

    padZero(num) {
      return String(num).padStart(2, '0');
    }

    showExpired() {
      this.destroy();
      // Opt-in teardown: without this the bar stands forever announcing a finished promotion on
      // every page until a human edits the theme. Default (flag absent) keeps the old behaviour.
      if (this.element.dataset.hideWhenExpired === 'true') {
        this.element.style.display = 'none';
        return;
      }
      if (this.containerEl) this.containerEl.style.display = 'none';
      if (this.expiredEl) {
        if (this.expiredMessage) {
          var span = document.createElement('span');
          span.className = 'countdown-timer__expired';
          span.textContent = this.expiredMessage;
          this.expiredEl.innerHTML = '';
          this.expiredEl.appendChild(span);
        }
        this.expiredEl.style.display = 'block';
      }
    }
  }

  // Initialize all countdown elements
  function initCountdowns(root) {
    root = root || document;
    var elements = root.querySelectorAll('.promotional-banner--countdown, [data-countdown]');
    elements.forEach(function(element) {
      new PromotionalCountdown(element);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { initCountdowns(); });
  } else {
    initCountdowns();
  }

  // Shopify Design Mode support
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      initCountdowns(event.target);
    });
  }

  window.PromotionalCountdown = PromotionalCountdown;
}
